import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { authColors } from '../../styles/colors';
import { plansService } from '../../services/supabase';
import { clearCheckoutState, getCheckoutBillingType, getCheckoutPurchaseEventId } from '../../utils/checkoutStorage';
import { trackMetaPurchase } from '../../utils/metaPixel';
import { trackOpenAIPurchase } from '../../utils/openaiPixel';
import Spinner from '../../components/ui/Spinner';

/**
 * Standard SaaS billing return page:
 * 
 * Flow:
 * 1. Stripe redirects here after successful checkout
 * 2. Webhook updates DB asynchronously (can take 4-5s)
 * 3. This page polls DB until plan is updated, then redirects to /app
 * 
 * Polling strategy:
 * - Check every 1.5s, max 6 attempts (9s total)
 * - If plan becomes 'pro', redirect immediately
 * - If timeout, redirect anyway (webhook will catch up)
 */

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 6;

type PageState = 'loading' | 'confirming' | 'confirmed' | 'unauthenticated';

const BillingCheckoutReturn: React.FC = () => {
  const { t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const [pageState, setPageState] = useState<PageState>('loading');
  const redirectingRef = useRef(false);
  const pollCountRef = useRef(0);
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    // Wait for auth to resolve
    if (authLoading) return;

    // If not authenticated, show sign-in prompt
    if (!user) {
      setPageState('unauthenticated');
      return;
    }

    // Prevent double execution
    if (redirectingRef.current) return;
    redirectingRef.current = true;

    // Clean up checkout sessionStorage flags (keep billing type until Purchase is tracked)
    const billingType = getCheckoutBillingType() ?? 'monthly';
    clearCheckoutState();

    setPageState('confirming');

    const trackPurchaseOnce = () => {
      if (purchaseTrackedRef.current) return;
      purchaseTrackedRef.current = true;
      const purchaseEventId = getCheckoutPurchaseEventId() ?? undefined;
      trackMetaPurchase(billingType, purchaseEventId);
      trackOpenAIPurchase(billingType, purchaseEventId);
    };

    // Poll for plan update
    const pollForPlanUpdate = async (): Promise<boolean> => {
      try {
        const usage = await plansService.getUserUsage({ force: true });
        return usage?.plan?.name === 'pro';
      } catch (error) {
        console.error('Error checking plan:', error);
        return false;
      }
    };

    const startPolling = async () => {
      while (pollCountRef.current < MAX_POLL_ATTEMPTS) {
        const isPro = await pollForPlanUpdate();
        
        if (isPro) {
          trackPurchaseOnce();
          // Plan confirmed - show success briefly then redirect
          setPageState('confirmed');
          setTimeout(() => {
            window.location.replace('/app');
          }, 800);
          return;
        }

        pollCountRef.current++;
        
        if (pollCountRef.current < MAX_POLL_ATTEMPTS) {
          // Wait before next attempt
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        }
      }

      // Timeout reached - redirect anyway (webhook will catch up)
      // User might see free quotas briefly, but it will update on next page load
      window.location.replace('/app');
    };

    startPolling();
  }, [authLoading, user]);

  // Loading state (waiting for auth)
  if (pageState === 'loading') {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <div className="text-center">
          <Spinner size="lg" label={t('common.loading')} className="justify-center" />
        </div>
      </div>
    );
  }

  // Confirming payment (polling)
  if (pageState === 'confirming') {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-4">
          <div className="flex justify-center">
            <Spinner size="lg" />
          </div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            {t('billing.processingTitle')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('billing.processingMessage')}
          </p>
        </div>
      </div>
    );
  }

  // Payment confirmed - brief success message before redirect
  if (pageState === 'confirmed') {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-4">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            {t('billing.confirmed')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('auth.redirectingToApp')}
          </p>
        </div>
      </div>
    );
  }

  // Unauthenticated - show sign-in prompt
  return (
    <div className={`min-h-screen flex flex-col ${authColors.backgroundGradient}`}>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('billing.paymentSuccessful')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('billing.signInToAccess')}
          </p>
          <Link
            to="/login?redirect=/app"
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors font-medium"
          >
            {t('auth.signIn')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BillingCheckoutReturn;
